/**
 * Reviews Carousel
 * Handles carousel functionality for homepage Google Reviews
 */

(function () {
  'use strict';

  class ReviewsCarousel {
    constructor() {
      this.track = document.getElementById('reviews-carousel-track');
      this.dotsContainer = document.getElementById('reviews-carousel-dots');

      if (!this.track) return;

      this.slides = this.track.querySelectorAll('.reviews-carousel-slide');
      this.prevBtn = document.querySelector('.reviews-carousel-btn-prev');
      this.nextBtn = document.querySelector('.reviews-carousel-btn-next');

      this.currentIndex = 0;
      this.slidesPerView = this.getSlidesPerView();
      this.totalSlides = this.slides.length;
      this.totalPages = Math.ceil(this.totalSlides / this.slidesPerView);
      this.currentPage = 0;

      // Touch/drag support
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.dragThreshold = 50;

      // Autoplay
      this.autoplayInterval = null;
      this.autoplaySpeed = 5000;
      this.isAutoplayEnabled = true;

      this.init();
    }

    init() {
      this.generateDots();
      this.bindEvents();
      this.updateButtons();
      this.updateDots();
      this.startAutoplay();
    }

    getSlidesPerView() {
      const width = window.innerWidth;
      if (width >= 990) return 3;
      if (width >= 750) return 2;
      return 1;
    }

    generateDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = '';
      this.totalPages = Math.ceil(this.totalSlides / this.slidesPerView);

      for (let i = 0; i < this.totalPages; i++) {
        const dot = document.createElement('button');
        dot.className = 'reviews-carousel-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', `Ga naar pagina ${i + 1}`);
        dot.setAttribute('data-page', i);
        dot.addEventListener('click', () => this.goToPage(i));
        this.dotsContainer.appendChild(dot);
      }

      this.dots = this.dotsContainer.querySelectorAll('.reviews-carousel-dot');
    }

    bindEvents() {
      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      // Touch events
      this.track.addEventListener('touchstart', (e) => this.onDragStart(e), { passive: true });
      this.track.addEventListener('touchmove', (e) => this.onDragMove(e), { passive: true });
      this.track.addEventListener('touchend', () => this.onDragEnd());

      // Mouse drag events
      this.track.addEventListener('mousedown', (e) => this.onDragStart(e));
      this.track.addEventListener('mousemove', (e) => this.onDragMove(e));
      this.track.addEventListener('mouseup', () => this.onDragEnd());
      this.track.addEventListener('mouseleave', () => this.onDragEnd());

      // Resize handler
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const newSlidesPerView = this.getSlidesPerView();
          if (newSlidesPerView !== this.slidesPerView) {
            this.slidesPerView = newSlidesPerView;
            this.generateDots();
            // Reset to valid page
            if (this.currentPage >= this.totalPages) {
              this.currentPage = this.totalPages - 1;
            }
            this.updateCarousel();
          }
        }, 150);
      });

      // Pause autoplay on hover
      this.track.parentElement.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.track.parentElement.addEventListener('mouseleave', () => this.resumeAutoplay());

      // Pause autoplay when not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAutoplay();
        } else {
          this.resumeAutoplay();
        }
      });

      // Keyboard navigation
      this.track.parentElement.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.prev();
        } else if (e.key === 'ArrowRight') {
          this.next();
        }
      });
    }

    prev() {
      if (this.currentPage > 0) {
        this.currentPage--;
        this.updateCarousel();
      }
    }

    next() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage++;
        this.updateCarousel();
      } else if (this.isAutoplayEnabled) {
        // Loop back to start for autoplay
        this.currentPage = 0;
        this.updateCarousel();
      }
    }

    goToPage(pageIndex) {
      this.currentPage = Math.min(pageIndex, this.totalPages - 1);
      this.updateCarousel();
    }

    updateCarousel() {
      // Calculate offset based on page and slides per view
      const slideWidth = 100 / this.slidesPerView;
      const offset = this.currentPage * this.slidesPerView * slideWidth;

      // Don't scroll past the last slide
      const maxOffset = (this.totalSlides - this.slidesPerView) * slideWidth;
      const clampedOffset = Math.min(offset, maxOffset);

      this.track.style.transform = `translateX(-${clampedOffset}%)`;

      this.updateButtons();
      this.updateDots();
    }

    updateButtons() {
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentPage === 0;
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.currentPage >= this.totalPages - 1;
      }
    }

    updateDots() {
      if (!this.dots) return;

      this.dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentPage);
      });
    }

    // Touch/Drag handlers
    onDragStart(e) {
      this.isDragging = true;
      this.startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
      this.track.classList.add('is-dragging');
      this.pauseAutoplay();
    }

    onDragMove(e) {
      if (!this.isDragging) return;
      this.currentX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    }

    onDragEnd() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.track.classList.remove('is-dragging');

      const diff = this.startX - this.currentX;

      if (Math.abs(diff) > this.dragThreshold) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }

      this.resumeAutoplay();
    }

    // Autoplay
    startAutoplay() {
      if (!this.isAutoplayEnabled) return;

      this.autoplayInterval = setInterval(() => {
        this.next();
      }, this.autoplaySpeed);
    }

    pauseAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    resumeAutoplay() {
      if (this.isAutoplayEnabled && !this.autoplayInterval) {
        this.startAutoplay();
      }
    }
  }

  // Initialize on DOM ready
  function init() {
    new ReviewsCarousel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();